import { ChangeEvent, useReducer } from 'react';
import { RenderOptionProps } from '@trukt/ui/CompoundFilter';
import { SelectOption, SelectOptionProps } from '@trukt/ui/SelectOption';
import { TextField } from '@trukt/ui/TextField';
import { NullableKeys } from '@trukt/types/NullableKeys';
import { useAddressQueryWithApi } from '@trukt/react-hooks/useAddressQueryWithApi';
import { Option } from '@trukt/types/Option';
import { trimbleMapsSingleSearchApi } from '@api';
import { TrimbleMapsSingleSearchResponse } from '@trukt/portal-utils/TrimbleMapsSingleSearchApi';
import { OrderListFilterPickupOptions } from '@components/pages/OrderListView/hooks/use-order-list-compound-filter';

type CoordinatesLocation = {
  longitude: number;
  latitude: number;
  radius: number;
};
type AddressLocation = NullableKeys<{
  street1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}>;

type LabelValue = Pick<Option, 'label' | 'value'>;

export type LocationRadiusFilterData =
  (LabelValue & CoordinatesLocation) | (LabelValue & AddressLocation);

type Action = {
  type: 'UPDATE_ADDRESS' | 'UPDATE_RADIUS';
  payload: string;
};

type State = {
  address: string;
  radius: string;
};

export const LocationFilter = ({ addOption }: RenderOptionProps) => {
  // eslint-disable-next-line no-use-before-define
  const [state, dispatch] = useReducer(reducer, {
    address: '',
    radius: '',
  });
  const { query } = useAddressQueryWithApi(trimbleMapsSingleSearchApi, state.address);

  const handleAddressInputChange: SelectOptionProps['onDebouncedInputChange'] = (value, event) => {
    if (event.action !== 'input-change') return;
    dispatch({ type: 'UPDATE_ADDRESS', payload: value });
  };

  const handleSelectOption: SelectOptionProps<
    string,
    TrimbleMapsSingleSearchResponse['Locations'][0]
  >['onChange'] = (option, event) => {
    if (event.action !== 'select-option') return;

    addOption(buildLocationRadiusOption(buildDataFromLocation(option, state.radius)));
    dispatch({ type: 'UPDATE_RADIUS', payload: '' });
  };

  const handleRadiusChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'UPDATE_RADIUS', payload: target.value });
  };

  return (
    <div className="flex min-w-96 gap-2">
      <SelectOption
        className="min-w-80"
        variant="search"
        placeholder="Address"
        aria-label="Address input"
        isLoading={query.isLoading}
        options={query.data}
        onChange={handleSelectOption}
        onDebouncedInputChange={handleAddressInputChange}
      />
      <TextField
        variant="number"
        placeholder="Mi"
        aria-label="Radius input"
        value={state.radius}
        onChange={handleRadiusChange}
      />
    </div>
  );
};

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'UPDATE_ADDRESS':
      return { ...state, address: action.payload };

    case 'UPDATE_RADIUS':
      return { ...state, radius: action.payload };

    default:
      return state;
  }
};

export function buildLocationRadiusOption(data: LocationRadiusFilterData) {
  return {
    id: data.value,
    label: data.label,
    data,
    stateModifiers: {
      isActive: true,
    },
  };
}

export function buildDataFromLocation(
  option: Option<string, TrimbleMapsSingleSearchResponse['Locations'][0]>,
  miles: string,
): LocationRadiusFilterData {
  const radius = miles ? parseFloat(miles) : undefined;
  const isRadiusValid = radius && !Number.isNaN(radius);
  const address: AddressLocation | CoordinatesLocation = isRadiusValid
    ? {
        latitude: option.raw?.Coords.Lat ? parseFloat(option.raw.Coords.Lat) : 0,
        longitude: option.raw?.Coords.Lon ? parseFloat(option.raw.Coords.Lon) : 0,
        radius,
      }
    : {
        street1: option.raw?.Address?.StreetAddress || undefined,
        city: option.raw?.Address?.City || undefined,
        state: option.raw?.Address?.State || undefined,
        zipCode: option.raw?.Address?.Zip || undefined,
        country: option.raw?.Address?.Country || undefined,
      };

  return {
    label: isRadiusValid ? option.label.concat(` | ${radius} Mi`) : option.label,
    value: option.value,
    ...address,
  };
}

export function extractLocationRadiusData(option: OrderListFilterPickupOptions[0]): {
  miles: string;
  location: TrimbleMapsSingleSearchResponse['Locations'][0];
} {
  const { label: _, value: __, ...data } = option?.data ?? { label: '', value: '' };

  if (isCoordinatesLocation(data)) {
    return {
      miles: data.radius.toString(),
      // @ts-expect-error - this are the only values available here
      location: { Coords: { Lat: data?.latitude.toString(), Lon: data?.longitude.toString() } },
    };
  }

  return {
    miles: '',
    location: {
      // @ts-expect-error - this are the only values available here
      Address: {
        StreetAddress: data?.street1 ?? '',
        City: data?.city ?? '',
        State: data?.state ?? '',
        Zip: data?.zipCode ?? '',
        Country: data?.country ?? '',
      },
    },
  };
}

function isCoordinatesLocation(
  data: Omit<LocationRadiusFilterData, 'label' | 'value'>,
): data is CoordinatesLocation {
  return 'latitude' in data && 'longitude' in data && 'radius' in data;
}
